import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CustomerPlanDiscount } from './api'
import { createPlanDiscount, deleteDiscount, getPlanDiscount } from './endpoints'

const queryKeys = {
  customerPlanDiscount: (customerId: string) => ['customer-plan-discount', customerId] as const,
  createCustomerPlanDiscount: (customerId: string) => ['create-customer-plan-discount', customerId] as const,
  deleteCustomerDiscount: (customerId: string) => ['delete-customer-plan-discount', customerId] as const,
}

export const useCustomerPlanDiscount = (customerId: string) => {
  return useQuery({
    queryKey: queryKeys.customerPlanDiscount(customerId),
    queryFn: () => getPlanDiscount(customerId),
  })
}

export const useCreateCustomerPlanDiscount = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.createCustomerPlanDiscount(customerId),
    mutationFn: (flatAndPerUnitDiscount: CustomerPlanDiscount) => createPlanDiscount(customerId, flatAndPerUnitDiscount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerPlanDiscount(customerId) })
    },
  })
}

export const useDeleteCustomerPlanDiscount = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.deleteCustomerDiscount(customerId),
    mutationFn: () => deleteDiscount(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerPlanDiscount(customerId) })
    },
  })
}

interface InvalidateCustomerPlanDiscountParams {
  queryClient: QueryClient
  customerId: string
}

export const invalidateCustomerPlanDiscount = ({ queryClient, customerId }: InvalidateCustomerPlanDiscountParams) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.customerPlanDiscount(customerId) })
}
